import { Routes } from '@angular/router';
import { UserAuthorizationGuard } from '@modules/auth/authentication-guard';
import { companyRegistrationGuard } from '@shared/guards/company-registration.guard';
import { settingRoutes } from '@modules/settings/settings.route';
import { planningRoutes } from '@modules/planning/planning.route';
import { demandRequirementsRoutes } from '@modules/demand-requirements/demand-requirements.route';
import { routes as demandForecastRoutes } from '@modules/demand-forecast/demand-forecast.route';
import { userRoutes } from '@modules/user-management/user.route';
import { companyRoutes } from '@modules/companies/companies.route';
import { registrationLetterRoutes } from '@modules/registration-letters/registration-letters.route';
import { UserType } from '@shared/enums/user-type';
import { applicationsRoutes } from '@modules/applications/applications.route';
import { roleRoutes } from '@modules/role-management/role.route';
import { reportsRoutes } from '@modules/reports/reports.route';
import { CompanyManagement } from '@modules/external-services/company-management/company-management';
import { billingRoutes } from '@modules/billing/billing.route';
import { FinanceManagement } from '@modules/external-services/finance-management/finance-management';
import { TerminalOperatorManagement } from '@modules/external-services/terminal-operator-management/terminal-operator-management';
import { enforcementRequestsRoutes } from '@modules/enforcement-requests/enforcement-requests.route';
import { BANK_GUARANTEE_SUBMISSIONS_ROUTES } from '@modules/bank-guarantee-submissioons/routes/bank-guarantee-submissioons.routes';
import { OmcManagement } from '@modules/external-services/omc-management/omc-management';
import { authRoutes } from '@modules/auth/auth.routes';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { AUTH_REDIRECT_ROUTE } from '@shared/constants/fields.constants';
import { SMART_FORECASTING_ROUTES } from '@modules/smart-forecasting/routes/smart-forecasting.routes';
import { tenderPlanningRoutes } from '@modules/tender-planning/tender-planning.routes';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('@modules/landing-page-premium/landing-page-premium').then(
        (c) => c.LandingPagePremiumComponent,
      ),
  },
  {
    path: 'page-two',
    pathMatch: 'full',
    loadComponent: () => import('@modules/landing-page/landing-page').then((c) => c.LandingPage),
  },
  ...authRoutes,
  {
    path: 'services-selection',
    loadComponent: () =>
      import('@modules/service-selection/service-selection').then((c) => c.ServiceSelection),
    canActivate: [UserAuthorizationGuard, companyRegistrationGuard],
    data: {
      allowedUserTypes: [UserType.EXTERNAL_USER],
      breadcrumb: 'Company Roles & Permissions Management',
    },
  },
  {
    path: 'prequalification',
    loadComponent: () =>
      import('@modules/registration/supplier-registration/prequalification-window/prequalification-window').then(
        (c) => c.PrequalificationWindow,
      ),
    data: { allowedUserTypes: [UserType.EXTERNAL_USER] },
    canActivate: [UserAuthorizationGuard, companyRegistrationGuard],
  },
  {
    path: 'external-services',
    loadComponent: () =>
      import('@layouts/external-authenticated-layout/external-authenticated-layout').then(
        (c) => c.ExternalAuthenticatedLayout,
      ),
    canActivate: [UserAuthorizationGuard, companyRegistrationGuard],
    data: { allowedUserTypes: [UserType.EXTERNAL_USER] },
    children: [
      {
        path: 'company-management',
        component: CompanyManagement,
        loadChildren: () =>
          import('@modules/external-services/company-management/routes/company-management.routes').then(
            (c) => c.COMPANY_MANAGEMENT_ROUTES,
          ),
      },
      {
        path: 'finance-management',
        component: FinanceManagement,
        loadChildren: () =>
          import('@modules/external-services/finance-management/routes/finance-management.routes').then(
            (c) => c.FINANCE_MANAGEMENT_ROUTES,
          ),
      },
      {
        path: 'terminal-operator-management',
        component: TerminalOperatorManagement,
        loadChildren: () =>
          import('@modules/external-services/terminal-operator-management/routes/terminal-operator-management.routes').then(
            (c) => c.TERMINAL_OPERATOR_MANAGEMENT_ROUTES,
          ),
      },
      {
        path: 'non-bps-management',
        loadComponent: () =>
          import('@modules/external-services/non-bps-management/non-bps-management').then(
            (c) => c.NonBpsManagement,
          ),
        children: [
          {
            path: '',
            loadChildren: () =>
              import('@modules/external-services/non-bps-management/routes/non-bps-management.routes').then(
                (m) => m.NON_BPS_MANAGEMENT_ROUTES,
              ),
          },
        ],
      },
      {
        path: 'supplier-management',
        loadComponent: () =>
          import('@modules/external-services/supplier-management/supplier-management').then(
            (c) => c.SupplierManagement,
          ),
        children: [
          {
            path: '',
            loadChildren: () =>
              import('@modules/external-services/supplier-management/routes/supplier-management.routes').then(
                (m) => m.SUPPLIER_MANAGEMENT_ROUTES,
              ),
          },
        ],
      },
      {
        path: 'supplier-prequalification-advertisements',
        loadComponent: () =>
          import('@modules/external-services/supplier-prequalification-advertisements/supplier-prequalification-advertisements').then(
            (c) => c.SupplierPrequalificationAdvertisementsComponent,
          ),
      },
      {
        path: 'omc-management',
        component: OmcManagement,
        loadChildren: () =>
          import('@modules/external-services/omc-management/routes/omc-management-routes').then(
            (m) => m.omcManagementRoutes,
          ),
      },
      {
        path: 'shipping-agent-management',
        loadComponent: () =>
          import('@modules/external-services/shipping-agent-management/shipping-agent-management').then(
            (c) => c.ShippingAgentManagement,
          ),
        loadChildren: () =>
          import('@modules/external-services/shipping-agent-management/routes/shipping-agent-management.routes').then(
            (m) => m.SHIPPING_AGENT_MANAGEMENT_ROUTES,
          ),
      },
    ],
  },
  {
    path: 'company-profile',
    loadComponent: () =>
      import('@modules/company-profile/company-profile').then((c) => c.CompanyProfile),
    canActivate: [UserAuthorizationGuard],
    data: { allowedUserTypes: [UserType.EXTERNAL_USER] },
    children: [
      {
        path: '',
        redirectTo: 'company-type-selection',
        pathMatch: 'full',
      },
      {
        path: 'company-profile-local-registration',
        loadComponent: () =>
          import('@modules/company-profile/company-profile-local/company-profile-local').then(
            (c) => c.CompanyProfileLocal,
          ),
      },
      {
        path: 'company-profile-international-registration',
        loadComponent: () =>
          import('@modules/company-profile/company-profile-international/company-profile-international').then(
            (c) => c.CompanyProfileInternational,
          ),
      },
      {
        path: 'company-type-selection',
        loadComponent: () =>
          import('@modules/company-profile/company-type-selection/company-type-selection').then(
            (c) => c.CompanyTypeSelection,
          ),
      },
    ],
  },
  {
    path: 'mis',
    loadComponent: () =>
      import('@layouts/authenticated-layout/authenticated-layout').then(
        (c) => c.AuthenticatedLayout,
      ),
    canActivate: [UserAuthorizationGuard],
    data: { allowedUserTypes: [UserType.INTERNAL_USER] },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
      {
        path: 'home',
        loadComponent: () =>
          import('@modules/portal-home/portal-home.component').then((c) => c.PortalHomeComponent),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('@modules/dashboard/procurement-dashboard').then((c) => c.ProcurementDashboard),
      },
      {
        path: 'supplier-pre-qualifications/new',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/supplier-pre-qualification-create/supplier-pre-qualification-create').then(
            (c) => c.SupplierPreQualificationCreateComponent,
          ),
      },
      {
        path: 'supplier-pre-qualifications/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/supplier-pre-qualification-details/supplier-pre-qualification-details').then(
            (c) => c.SupplierPreQualificationDetailsComponent,
          ),
      },
      {
        path: 'supplier-pre-qualifications',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/supplier-pre-qualifications').then(
            (c) => c.SupplierPreQualificationsComponent,
          ),
      },
      {
        path: 'checklists',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/checklists/checklists.component').then(
            (c) => c.ChecklistsComponent,
          ),
      },
      {
        path: 'checklists/:id/configure',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/checklists/checklist-configuration/checklist-configuration.component').then(
            (c) => c.ChecklistConfigurationComponent,
          ),
      },
      {
        path: 'due-diligences',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/due-diligences/due-diligences.component').then(
            (c) => c.DueDiligencesComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_DUE_DILIGENCE'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'due-diligence-reports',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/due-diligences/due-diligence-reports/due-diligence-reports.component').then(
            (c) => c.DueDiligenceReportsComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_DUE_DILIGENCE'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'due-diligence-reports/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/due-diligences/due-diligence-report-details/due-diligence-report-details.component').then(
            (c) => c.DueDiligenceReportDetailsComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_DUE_DILIGENCE'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'due-diligences/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/due-diligences/due-diligence-details/due-diligence-details.component').then(
            (c) => c.DueDiligenceDetailsComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_DUE_DILIGENCE'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'due-diligences/:id/due-diligence-team',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/due-diligences/due-diligence-team/due-diligence-team.component').then(
            (c) => c.DueDiligenceTeamComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_DUE_DILIGENCE'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'assessment-notices',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/assessment-notices/assessment-notices.component').then(
            (c) => c.AssessmentNoticesComponent,
          ),
      },
      {
        path: 'assessment-notices/:uuid/edit',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/assessment-notices/assessment-notice-edit/assessment-notice-edit.component').then(
            (c) => c.AssessmentNoticeEditComponent,
          ),
      },
      {
        path: 'prequalification-windows',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-windows.component').then(
            (c) => c.PrequalificationWindowsComponent,
          ),
      },
      {
        path: 'prequalification-window-extensions',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-extensions/prequalification-window-extensions.component').then(
            (c) => c.PrequalificationWindowExtensionsComponent,
          ),
      },
      {
        path: 'prequalification-window-extensions/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-extension-details/prequalification-window-extension-details.component').then(
            (c) => c.PrequalificationWindowExtensionDetailsComponent,
          ),
      },
      {
        path: 'prequalification-submissions',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-submissions/prequalification-submissions.component').then(
            (c) => c.PrequalificationSubmissionsComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_EVALUATE_PREQUALIFICATION'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'prequalification-submissions/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-submissions/prequalification-submission-details/prequalification-submission-details.component').then(
            (c) => c.PrequalificationSubmissionDetailsComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_EVALUATE_PREQUALIFICATION'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'prequalification-windows/new',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-form/prequalification-window-form.component').then(
            (c) => c.PrequalificationWindowFormComponent,
          ),
      },
      {
        path: 'prequalification-windows/:id/edit',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-form/prequalification-window-form.component').then(
            (c) => c.PrequalificationWindowFormComponent,
          ),
      },
      {
        path: 'prequalification-windows/:id/submissions',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-submissions/prequalification-window-submissions.component').then(
            (c) => c.PrequalificationWindowSubmissionsComponent,
          ),
      },
      {
        path: 'prequalification-windows/:id/evaluation-team',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-evaluation-team/prequalification-window-evaluation-team.component').then(
            (c) => c.PrequalificationWindowEvaluationTeamComponent,
          ),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_REGISTRATION_EVALUATION_TEAM_VIEW'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'prequalification-windows/:id/evaluation-report',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-evaluation-report/prequalification-window-evaluation-report.component').then(
            (c) => c.PrequalificationWindowEvaluationReportComponent,
          ),
      },
      {
        path: 'evaluation-reports/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-evaluation-report/prequalification-window-evaluation-report.component').then(
            (c) => c.PrequalificationWindowEvaluationReportComponent,
          ),
      },
      {
        path: 'evaluation-reports',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-evaluation-reports/prequalification-window-evaluation-reports.component').then(
            (c) => c.PrequalificationWindowEvaluationReportsComponent,
          ),
      },
      {
        path: 'prequalification-windows/:id',
        loadComponent: () =>
          import('@modules/supplier-pre-qualifications/prequalification-windows/prequalification-window-details/prequalification-window-details.component').then(
            (c) => c.PrequalificationWindowDetailsComponent,
          ),
      },
      {
        path: 'terminal-operators',
        loadComponent: () =>
          import('@modules/terminal-operators/terminal-operators').then(
            (c) => c.TerminalOperatorsComponent,
          ),
        loadChildren: () =>
          import('@modules/terminal-operators/routes/terminal-operators.routes').then(
            (m) => m.TERMINAL_OPERATORS_ROUTES,
          ),
      },
      {
        path: 'non-bps-operators',
        loadComponent: () =>
          import('@modules/non-bps-operators/non-bps-operators').then(
            (c) => c.NonBpsOperatorsComponent,
          ),
      },
      {
        path: 'weekly-stock-balance',
        loadComponent: () =>
          import('@modules/weekly-stock-balances/weekly-stock-balances').then(
            (c) => c.WeeklyStockBalances,
          ),
      },
      {
        path: 'weekly-stock-balance/:id',
        loadComponent: () =>
          import('@modules/weekly-stock-balances/weekly-stock-balance-details/weekly-stock-balance-details').then(
            (c) => c.WeeklyStockBalanceDetails,
          ),
      },
      {
        path: 'product-categories',
        loadComponent: () =>
          import('@modules/products/product-categories').then((c) => c.ProductCategoriesComponent),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: ['ROLE_VIEW_PRODUCT_CATEGORY'],
            redirectTo: AUTH_REDIRECT_ROUTE,
          },
        },
      },
      {
        path: 'products',
        loadComponent: () => import('@modules/products/products').then((c) => c.ProductsComponent),
      },
      {
        path: 'product-usages',
        loadComponent: () =>
          import('@modules/products/product-usages').then((c) => c.ProductUsagesComponent),
      },
      {
        path: 'product-parameters',
        loadComponent: () =>
          import(
            '@modules/planning/planning-parameters/product-parameters/product-parameters.component'
          ).then((c) => c.ProductParametersComponent),

      },
      {
        path: 'product-conversions',
        loadComponent: () =>
          import('@modules/products/product-unit-conversions/product-unit-conversions.component').then(
            (c) => c.ProductUnitConversionsComponent,
          ),
      },
      {
        path: 'omc-product-market-shares',
        loadComponent: () =>
          import('@modules/products/omc-product-market-shares/omc-product-market-shares.component').then(
            (c) => c.OmcProductMarketSharesComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('@modules/profile/internal-profile').then((c) => c.InternalProfileComponent),
      },
      ...settingRoutes,
      ...demandRequirementsRoutes,
      ...tenderPlanningRoutes,
      ...roleRoutes,
      ...userRoutes,
      ...companyRoutes,
      ...registrationLetterRoutes,
      ...applicationsRoutes,
      ...reportsRoutes,
      ...billingRoutes,
      ...settingRoutes,
      ...planningRoutes,
      ...demandForecastRoutes,
      ...enforcementRequestsRoutes,
      ...BANK_GUARANTEE_SUBMISSIONS_ROUTES,
      ...SMART_FORECASTING_ROUTES,
      {
        path: 'notifications',
        loadChildren: () =>
          import('./modules/notifications/notification-template/notification-template.routes').then(
            (m) => m.NOTIFICATION_ROUTES,
          ),
      },
      {
        path: 'workflows',
        loadChildren: () =>
          import('./modules/workflow/workflow.routes').then((m) => m.WORKFLOW_ROUTES),
      },
      {
        path: 'financial-security-configurations',
        loadChildren: () =>
          import('@modules/financial-security-configuration/routes/financial-security-configuration.routes').then(
            (m) => m.FINANCIAL_SECURITY_CONFIGURATION_ROUTES,
          ),
      },
      {
        path: 'tenders',
        loadChildren: () =>
          import('./modules/tender-packages/tender-packages.routes').then(
            (m) => m.TENDER_PACKAGES_ROUTES,
          ),
      },
      {
        path: 'tender-packages-bak',
        loadChildren: () =>
          import('./modules/tender-packages-bak/routes/tender-preparation.routes').then(
            (m) => m.TENDER_PREPARATION_ROUTES,
          ),
      },
    ],
  },
  {
    path: 'applicants',
    loadComponent: () =>
      import('@layouts/external-authenticated-layout/external-authenticated-layout').then(
        (c) => c.ExternalAuthenticatedLayout,
      ),
    canActivate: [UserAuthorizationGuard, companyRegistrationGuard],
    data: { allowedUserTypes: [UserType.EXTERNAL_USER] },
    children: [
      {
        path: 'registration',
        loadComponent: () =>
          import('@modules/registration/registration').then((c) => c.Registration),
        loadChildren: () =>
          import('@modules/registration/routes/registration.routes').then(
            (c) => c.REGISTRATION_ROUTES,
          ),
      },
    ],
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('@modules/auth/unauthorize/unauthorize').then((c) => c.Unauthorize),
  },
  { path: '**', redirectTo: '' },
];
